'use client';
import { faqData } from '@/lib/constant/faqs.constants';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

export default function Faqs() {
  // Use a single state for active index. Start with 0 for desktop default.
  const [activeIndex, setActiveIndex] = useState<number | null>(0);

  // Toggle function for accordion behavior (mobile) and selection (desktop)
  const handleToggle = (index: number) => {
    setActiveIndex(prevIndex => (prevIndex === index ? null : index));
  };

  return (
    <div
      id="faq"
      className="w-full min-h-fit dark:bg-gray-900/40 bg-gray-50/50 md:px-16 px-5 flex flex-col items-center py-16 md:py-24"
    >
      {/* Section Header */}
      <div className="w-full max-w-7xl mx-auto text-center mb-12">
        <h2 className="font-bold md:text-4xl text-3xl mb-3 dark:text-white">
          Frequently Asked Questions
        </h2>
        <p className="md:text-base text-sm font-medium text-gray-600 dark:text-gray-400">
          Find quick answers to common questions about Job Assure.
        </p>
      </div>

      {/* Main Content Area: Flex container for columns */}
      <div className="w-full max-w-7xl mx-auto h-fit flex flex-col md:flex-row gap-8">

        {/* Left Column / Mobile Combined List */}
        <div className="w-full md:w-1/3 flex flex-col gap-2">
          {faqData.map((faq, i) => (
            <div key={i} className="w-full flex flex-col">
              {/* Question Button */}
              <button
                onClick={() => handleToggle(i)}
                aria-expanded={activeIndex === i}
                className={`group w-full flex justify-between items-center text-left p-4 rounded-lg transition-all duration-200 ease-in-out focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 dark:focus-visible:ring-accent/50 ${activeIndex === i
                    ? 'bg-primary/10 dark:bg-primary/20 text-primary dark:text-white'
                    : 'bg-white dark:bg-gray-800/60 hover:bg-gray-100 dark:hover:bg-gray-700/60 text-gray-700 dark:text-gray-300'
                  }`}
              >
                <span className="font-medium text-sm md:text-base">
                  {faq.question}
                </span>
                {/* Desktop Icon */}
                <ChevronRight
                  className={`hidden md:block w-5 h-5 transition-transform duration-200 ease-in-out ${activeIndex === i
                      ? 'translate-x-1 text-primary dark:text-white'
                      : 'text-gray-400 group-hover:text-gray-600 dark:group-hover:text-gray-200'
                    }`}
                />
                {/* Mobile Icon */}
                <motion.div
                  className="block md:hidden"
                  animate={{ rotate: activeIndex === i ? 180 : 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <ChevronDown
                    className={`w-5 h-5 ${activeIndex === i
                        ? 'text-primary dark:text-white'
                        : 'text-gray-400 group-hover:text-gray-600 dark:group-hover:text-gray-200'
                      }`}
                  />
                </motion.div>
              </button>

              {/* Mobile Answer (Accordion Content) */}
              <AnimatePresence initial={false}>
                {activeIndex === i && (
                  <motion.div
                    key="content"
                    initial="collapsed"
                    animate="open"
                    exit="collapsed"
                    variants={{
                      open: { opacity: 1, height: 'auto' },
                      collapsed: { opacity: 0, height: 0 },
                    }}
                    transition={{ duration: 0.3, ease: 'easeInOut' }}
                    className="overflow-hidden md:hidden w-full" // Hide on medium screens and up
                  >
                    <div className="pt-3 pl-1 pr-1">
                       <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed bg-white dark:bg-gray-800/60 p-4 rounded-md">
                         {faq.answer}
                       </p>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ))}
        </div>

        {/* Right Column: Desktop Answer Display */}
        <div className="hidden md:flex md:w-2/3 bg-white dark:bg-gray-800/60 rounded-lg p-6 md:p-8 shadow-sm min-h-[300px] items-start">
          <AnimatePresence mode="wait">
            {activeIndex !== null ? (
              <motion.div
                key={activeIndex} // Key change triggers animation
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.3, ease: 'easeInOut' }}
                className="w-full"
              >
                <h3 className="font-semibold text-lg md:text-xl mb-3 text-gray-900 dark:text-white">
                  {faqData[activeIndex].question}
                </h3>
                <p className="md:text-base text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                  {faqData[activeIndex].answer}
                  {/* //TODO: Add short video explanation regarding the answer */}
                </p>
              </motion.div>
            ) : (
              // Optional: Placeholder when no item is selected on desktop (if toggle logic allows null)
              <div className="text-gray-500 dark:text-gray-400 flex items-center justify-center h-full w-full">
                Select a question to see the answer.
              </div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}
